import COLORS from '../../../styles/js/variables/colors';

// React-select style callbacks receive plain CSS, so use the CSS variable form of Chakra's layer tokens.
const DROPDOWN_Z_INDEX = 'var(--udl-zIndices-dropdown, 1000)';
const POPOVER_Z_INDEX = 'var(--udl-zIndices-popover, 1500)';

export const selectStyles = {
  clearIndicator: provided => ({
    ...provided,
    marginRight: '-5px',
    color: COLORS.foundryGray80,
    svg: {
      stroke: COLORS.paletteWhite,
      strokeWidth: '1px',
    },
  }),
  container: provided => ({
    ...provided,
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    minHeight: '55px',
  }),
  control: provided => ({
    ...provided,
    position: 'relative',
    padding: '26px 66px 0 14px',
    width: '100%',
    height: '100%',
    minHeight: '55px',
    border: 'none',
    borderRadius: 0,
    background: 'none',
  }),
  dropdownIndicator: provided => ({
    ...provided,
    marginLeft: '-5px',
    color: COLORS.foundryGray80,
    svg: {
      stroke: COLORS.paletteWhite,
      strokeWidth: '1px',
    },
  }),
  indicatorsContainer: provided => ({
    ...provided,
    position: 'absolute',
    right: '7px',
    top: '10px',
  }),
  indicatorSeparator: provided => ({
    ...provided,
    display: 'none',
  }),
  menu: provided => ({
    ...provided,
    marginTop: 0,
    zIndex: DROPDOWN_Z_INDEX,
  }),
  // Dialog's scroll-lock sets pointer-events: none on <body>; a body-portaled menu must opt back in.
  menuPortal: base => ({ ...base, zIndex: POPOVER_Z_INDEX, position: 'fixed', pointerEvents: 'auto' }),
  multiValue: provided => ({
    ...provided,
    margin: '0 6px 6px 0',
    borderRadius: '2px',
    padding: '4px 8px 4px 14px',
    display: 'inline-flex',
    alignItems: 'flex-start',
  }),
  multiValueLabel: () => ({
    paddingTop: '2px',
  }),
  multiValueRemove: () => ({
    flexShrink: 0,
    flexGrow: 0,
    margin: '-2px 0 0 10px',
    padding: 0,
    minHeight: 'auto',
    color: COLORS.ColorsTeal600,
    border: 'none',
    fontWeight: 500,
    svg: {
      height: '26px',
      width: '26px',
      stroke: COLORS.ColorsTeal200,
      strokeWidth: '1px',
    },
  }),
  option: (provided, { isSelected, isFocused, isDisabled }) => ({
    ...provided,
    color: COLORS.foundryGray80,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    pointerEvents: isDisabled ? 'none' : 'auto',
    border: isSelected ? `1px solid ${COLORS.foundryBlue}` : 'initial',
    background: isDisabled
      ? COLORS.foundryGray10
      : isSelected && !isFocused
        ? 'white'
        : isFocused
          ? COLORS.foundryGray20
          : 'initial',
  }),
  placeholder: provided => ({
    ...provided,
    marginLeft: 0,
    marginTop: '4px',
  }),
  valueContainer: provided => ({
    ...provided,
    paddingLeft: 0,
  }),
};

export const selectControlStyles = ({ isFloatOutside, isMultiSelect, value }) => ({
  ...selectStyles,
  ...(isFloatOutside && {
    control: provided => ({
      ...provided,
      ...selectStyles.control(provided),
      position: 'relative',
      padding: '0 66px 0 14px',
      width: '100%',
      height: '100%',
      borderRadius: 0,
      background: 'none',
    }),
    indicatorsContainer: provided => ({
      ...provided,
      ...selectStyles.indicatorsContainer(provided),
      position: 'absolute',
      right: '7px',
      top: '0px',
    }),
  }),
  ...(!value ||
    (Array.isArray(value) &&
      !value.length && {
        container: provided => ({
          ...provided,
          ...selectStyles.container(provided),
          maxHeight: '55px',
        }),
      })),
  ...(isMultiSelect
    ? {
        input: provided => ({
          ...provided,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }),
      }
    : {
        container: provided => ({
          ...provided,
          ...selectStyles.container(provided),
          maxHeight: '55px',
        }),
        control: provided => ({
          ...provided,
          ...selectStyles.control(provided),
          padding: '24px 66px 0 14px',
          display: 'inline',
          maxHeight: '55px',
        }),
        valueContainer: provided => ({
          ...provided,
          ...selectStyles.valueContainer(provided),
          padding: 0,
          display: 'inline',
        }),
        input: provided => ({
          ...provided,
          paddingTop: 0,
        }),
      }),
});

export const selectSortableControlStyles = {
  ...selectStyles,
  multiValueLabel: () => ({
    padding: '1px 12px',
  }),
  multiValue: provided => ({
    ...provided,
    margin: '4px 0',
    height: '32px',
    backgroundColor: '#c4f2f1',
    borderRadius: '2px',
    padding: '4px 12px',
    display: 'inline-flex',
    alignItems: 'flex-start',
  }),
  valueContainer: provided => ({
    ...provided,
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '0',
  }),
};

export const selectControlPaginationStyles = () => {
  const baseStyles = selectControlStyles({});
  return {
    ...baseStyles,
    container: provided => ({
      ...provided,
      ...baseStyles.container(provided),
      marginLeft: '6px',
      width: 'auto',
      minHeight: 'auto',
      maxHeight: 'auto',
    }),
    control: provided => ({
      ...provided,
      ...baseStyles.control(provided),
      padding: '0 40px 0 0',
      minHeight: 'auto',
    }),
    indicatorsContainer: provided => ({
      ...provided,
      ...baseStyles.indicatorsContainer(provided),
      top: '-8px',
    }),
  };
};

export const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    danger: COLORS.foundryRed,
    neutral10: COLORS.ColorsTeal200, // chip
    neutral50: COLORS.foundryGray60, // placeholder label
    neutral80: COLORS.foundryGray80, // cursor
    primary: COLORS.foundryGray20,
    primary25: COLORS.foundryGray20, // menu item bg
    primary50: COLORS.foundryGray20,
    primary75: COLORS.foundryGray20,
  },
});

export const selectMultiValueTheme = theme => ({
  ...theme,
  borderRadius: 4,
  colors: {
    primary: COLORS.foundryTeal100,
    primary75: COLORS.foundryTeal100,
    primary50: COLORS.foundryTeal100,
    primary25: COLORS.foundryTeal100,
    danger: COLORS.foundryTeal100,
    dangerLight: COLORS.foundryTeal100,
  },
  spacing: {
    baseUnit: 4,
    controlHeight: 38,
    menuGutter: 8,
  },
});

export const AsyncSelectBoxListStyles = {
  container: base => ({
    ...base,
    width: '100%',
  }),
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused
      ? 'var(--udl-colors-surface-primary, #2684FF)'
      : 'var(--udl-colors-border-default, #ccd3db)',
    backgroundColor: 'white',
  }),
  menu: base => ({
    ...base,
    marginTop: '10px',
    marginBottom: 0,
    boxShadow: 'none',
    position: 'relative',
  }),
  menuList: base => ({
    ...base,
    maxHeight: '300px',
    padding: 0,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'var(--udl-colors-surface-active, #f0f0f0)' : 'white',
    color: 'black',
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    ...(state.isFocused && {
      outline: '2px solid var(--udl-colors-surface-primary, #2684FF)',
      outlineOffset: '-2px',
    }),
    '&:active': {
      backgroundColor: 'var(--udl-colors-surface-primary, #2684FF)',
      color: 'white',
    },
  }),
  multiValue: base => ({
    ...base,
    backgroundColor: '#e6f3ff',
  }),
  multiValueLabel: base => ({
    ...base,
    color: '#0066cc',
  }),
  loadingMessage: base => ({
    ...base,
    // Style for the loading message inside the dropdown
    padding: '8px 12px',
  }),
};
